import { EntityRef } from '../../models/referenceData'
import { ReferenceDataSource } from 'src/app/public/reference-data/base-reference.service'
import { FilterParams } from '../../utilities/http-params'
import { ActiveFilterDisplay, FilterMeta, QueryParam } from './common'
import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'

export type EntityFilterConfig = Readonly<{
  resource: ReferenceDataSource
  allowMultiple?: boolean
  allowWildcards?: boolean
  constant?: boolean
}>

export type EntityFilterStateConfig = EntityFilterConfig & Readonly<{ meta: FilterMeta }>

export type EntityFilterState = Readonly<{
  entities: readonly EntityRef[]
  wildcards: readonly string[]
  config: EntityFilterStateConfig
}>

export const initialEntityFilterState: EntityFilterState = {
  entities: [],
  wildcards: [],
  config: {
    resource: 'companies',
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
  },
}

export type SetEntityFilterConfigAction = Readonly<{
  type: 'SET_ENTITY_FILTER_CONFIG_ACTION',
  payload: Pick<EntityFilterState, 'config'>,
  meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>
}>

export type AddEntityRefsAction = Readonly<{
  type: 'ADD_ENTITY_REFS_ACTION',
  payload: Pick<EntityFilterState, 'entities'>,
  meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>
}>

export type RemoveEntityRefsAction = Readonly<{
  type: 'REMOVE_ENTITY_REFS_ACTION',
  payload: Pick<EntityFilterState, 'entities'>,
  meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>
}>

export type SetEntityRefsAction = Readonly<{
  type: 'SET_ENTITY_REFS_ACTION',
  payload: Pick<EntityFilterState, 'entities'>,
  meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>
}>

export type AddEntityWildcardsAction = Readonly<{
  type: 'ADD_ENTITY_WILDCARDS_ACTION',
  payload: Pick<EntityFilterState, 'wildcards'>,
  meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>
}>

export type RemoveEntityWildcardsAction = Readonly<{
  type: 'REMOVE_ENTITY_WILDCARDS_ACTION',
  payload: Pick<EntityFilterState, 'wildcards'>,
  meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>
}>

export type SetEntityWildcardsAction = Readonly<{
  type: 'SET_ENTITY_WILDCARDS_ACTION',
  payload: Pick<EntityFilterState, 'wildcards'>,
  meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>
}>

export type EntityFilterAction =
  | SetEntityFilterConfigAction
  | AddEntityRefsAction
  | RemoveEntityRefsAction
  | SetEntityRefsAction
  | AddEntityWildcardsAction
  | RemoveEntityWildcardsAction
  | SetEntityWildcardsAction

export const entityFilterReducer = (state: EntityFilterState, action: EntityFilterAction): EntityFilterState => {
  switch (action.type) {
    case 'SET_ENTITY_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_ENTITY_REFS_ACTION':
      return {
        ...state,
        entities: state.config.allowMultiple
          ? action.payload.entities
          : [action.payload.entities[0]].filter(Boolean),
      }
    case 'REMOVE_ENTITY_REFS_ACTION':
      return {
        ...state,
        entities: state.entities.filter(se => !action.payload.entities.some(ae => ae.entity_code === se.entity_code)),
      }
    case 'ADD_ENTITY_REFS_ACTION':
      if (state.config.allowMultiple) {
        return {
          ...state,
          entities: [
            ...state.entities.filter(se => !action.payload.entities.some(ae => ae.entity_code === se.entity_code)),
            ...action.payload.entities.filter(ae => !state.entities.some(se => se.entity_code === ae.entity_code)),
          ],
        }
      }
      return {
        ...state,
        entities: state.entities[0]?.entity_code !== action.payload.entities[0]?.entity_code
          ? [action.payload.entities[0]].filter(Boolean)
          : [],
      }
    case 'SET_ENTITY_WILDCARDS_ACTION':
      return {
        ...state,
        wildcards: state.config.allowWildcards
          ? action.payload.wildcards
          : [],
      }
    case 'REMOVE_ENTITY_WILDCARDS_ACTION':
      return {
        ...state,
        wildcards: state.wildcards.filter(sw => !action.payload.wildcards.includes(sw)),
      }
    case 'ADD_ENTITY_WILDCARDS_ACTION':
      return {
        ...state,
        wildcards: state.config.allowWildcards
          ? [
            ...state.wildcards.filter(sw => !action.payload.wildcards.includes(sw)),
            ...action.payload.wildcards.filter(aw => !state.wildcards.includes(aw)),
          ] : [],
      }
    default:
      return state
  }
}

export class EntityFilter {
  readonly type = 'ENTITY'

  static SetEntityFilterConfigAction(
    payload: SetEntityFilterConfigAction['payload'],
    meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetEntityFilterConfigAction {
    return { type: 'SET_ENTITY_FILTER_CONFIG_ACTION', payload, meta }
  }

  static AddEntityRefsAction(
    payload: AddEntityRefsAction['payload'],
    meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>,
  ): AddEntityRefsAction {
    return { type: 'ADD_ENTITY_REFS_ACTION', payload, meta }
  }

  static RemoveEntityRefsAction(
    payload: RemoveEntityRefsAction['payload'],
    meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>,
  ): RemoveEntityRefsAction {
    return { type: 'REMOVE_ENTITY_REFS_ACTION', payload, meta }
  }

  static SetEntityRefsAction(
    payload: SetEntityRefsAction['payload'],
    meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetEntityRefsAction {
    return { type: 'SET_ENTITY_REFS_ACTION', payload, meta }
  }

  static AddEntityWildcardsAction(
    payload: AddEntityWildcardsAction['payload'],
    meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>,
  ): AddEntityWildcardsAction {
    return { type: 'ADD_ENTITY_WILDCARDS_ACTION', payload, meta }
  }

  static RemoveEntityWildcardsAction(
    payload: RemoveEntityWildcardsAction['payload'],
    meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>,
  ): RemoveEntityWildcardsAction {
    return { type: 'REMOVE_ENTITY_WILDCARDS_ACTION', payload, meta }
  }

  static SetEntityWildcardsAction(
    payload: SetEntityWildcardsAction['payload'],
    meta: Omit<EntityFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetEntityWildcardsAction {
    return { type: 'SET_ENTITY_WILDCARDS_ACTION', payload, meta }
  }

  state: EntityFilterState

  constructor(
    partialStateWithConfig: Partial<EntityFilterState> & Pick<EntityFilterState, 'config'>,
    private dispatch: (action: EntityFilterAction) => void,
  ) {
    this.state = { ...initialEntityFilterState, ...partialStateWithConfig }
  }

  apply(action: EntityFilterAction) {
    this.state = entityFilterReducer(this.state, action)
    return this
  }

  asParams(): FilterParams[] {
    return this.state.entities.length || this.state.wildcards.length
      ? [
        {
          name: this.state.config.meta.fieldName,
          values: [
            {
              operation: 'in=',
              value: [
                this.state.entities.map(e => e.entity_code),
                this.state.wildcards,
              ].flat().join(','),
            },
          ],
        },
      ]
      : []
  }

  asActiveDisplay(): ActiveFilterDisplay[] {
    return [
      this.state.entities.map((e) => ({
        headerName: this.state.config.meta.fieldDisplay,
        operation: '=',
        value: e.entity_name,
        constant: this.state.config.constant ?? false,
        remove: (() =>
          this.dispatch(EntityFilter.RemoveEntityRefsAction(
            { entities: [e] },
            this.state.config.meta,
          ))
        ),
      })),
      this.state.wildcards.map((wc) => ({
        headerName: this.state.config.meta.fieldDisplay,
        operation: 'like',
        value: wc,
        constant: this.state.config.constant ?? false,
        remove: (() =>
          this.dispatch(EntityFilter.RemoveEntityWildcardsAction(
            { wildcards: [wc] },
            this.state.config.meta,
          ),
          )),
      })),
    ].flat()
  }

  asCount() {
    return this.state.entities.length + this.state.wildcards.length
  }

  fromUrlQueryParams(
    params: readonly QueryParam[],
    entities: readonly EntityRef[],
    defaultVal: readonly EntityRef[] | string | null | undefined,
  ) {
    const qp = params.find((p) => p.name === this.state.config.meta.fieldName)
    let importedState: Pick<EntityFilterState, 'entities' | 'wildcards'> = { entities: [], wildcards: [] }
    if (qp?.value) {
      importedState = qp.value
        .split(',')
        .reduce((acc, str) => {
          const foundEntity = entities.find(e => e.entity_code === str)
          return foundEntity
            ? { ...acc, entities: [...acc.entities, foundEntity] }
            : { ...acc, wildcards: [...acc.wildcards, str] }
        },
        importedState,
        )
    }

    else if (defaultVal && typeof defaultVal === 'string') {
      importedState = defaultVal.split(',')
        .reduce((acc, str) => {
          const foundEntity = entities.find(e => e.entity_code === str)
          return foundEntity
            ? { ...acc, entities: [...acc.entities, foundEntity] }
            : { ...acc, wildcards: [...acc.wildcards, str] }
        },
        importedState,
        )
    }

    else if (defaultVal && typeof defaultVal !== 'string') {
      importedState = {
        entities: defaultVal.filter((de) => entities.some(e => e.entity_code === de.entity_code)),
        wildcards: [],
      }
    }

    this.dispatch(EntityFilter.SetEntityRefsAction(
      { entities: importedState.entities },
      this.state.config.meta,
    ))
    this.dispatch(EntityFilter.SetEntityWildcardsAction(
      { wildcards: importedState.wildcards },
      this.state.config.meta,
    ))
  }

  asUrlQueryParams(): QueryParam[] {
    return this.state.entities.length || this.state.wildcards.length
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: [
            this.state.entities.map((e) => e.entity_code),
            this.state.wildcards,
          ].flat().join(','),
        },
      ]
      : []
  }

  asDashboardQueryParams(): QueryParam[] {
    if (!this.state.entities.length && !this.state.wildcards.length) {
      return []
    }

    const list = [
      this.state.entities.map(e => e.entity_code),
      this.state.config.allowWildcards ? this.state.wildcards : [],
    ].flat()
      .filter(Boolean)
      .join(',')
    return [{
      name: this.state.config.meta.fieldName,
      value: this.state.config.allowWildcards
        ? encodeURIComponent(list)
        : list,
    }]
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    const allowedFields = [
      'eldc',
      'control_area',
      'company',
      'delivery_class',
      'cpnode',
      'ldc',
      'location',
      'loss_class',
      'meter_type',
      'profile_class',
      'retailer',
      'service_point_source',
      'supply_class',
    ]
    const codeFields = [
      'company',
    ]
    const isAllowed = allowedFields.some(af => this.state.config.meta.fieldName.startsWith(af))
    if (!isAllowed) {
      return {}
    }
    const fieldBase = this.state.config.meta.fieldName.replace(/(_uid)|(_code)$/, '')
    const isCode = codeFields.some(cf => fieldBase === cf)
    return isCode
      ? { [`${fieldBase}_code`]: this.state.entities }
      : { [`${fieldBase}_uid`]: this.state.entities }
  }
}
